import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { supabase } from '../services/supabase';
import { trackEvent } from '../services/analytics';

const CommunityPromptsPreview = ({ maxItems = 9 }) => {
  const [markdown, setMarkdown] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch('/prompttemplatecontent.md', { cache: 'no-cache' });
        const text = await res.text();
        if (!cancelled) setMarkdown(text || '');
      } catch (e) {
        if (!cancelled) setMarkdown('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!markdown) {
      setItems([]);
      return;
    }

    const lines = markdown.split('\n');
    let current = null;
    const heads = [];

    const cleanTitle = (raw) => {
      if (!raw) return '';
      let t = raw.replace(/\[.*?\]\s*/g, '').trim();
      if (t.includes(':')) {
        const parts = t.split(':').map(s => s.trim());
        const last = parts[parts.length - 1];
        if (/(Marketplace|Dashboard|Community|Knowledge Base|Chat|Scheduling|Downloads)/i.test(last)) {
          t = last;
        } else {
          t = last.length >= 8 ? last : t;
        }
      }
      return t.replace(/\s{2,}/g, ' ').trim();
    };

    const friendlyDescription = (title, body) => {
      const t = (title || '').toLowerCase();
      if (/analytics|dashboard|kpi|charts/.test(t)) return 'Analytics dashboard with KPIs, charts and scheduled reports.';
      if (/microblog|community|social/.test(t)) return 'Social microblog with posts, follows, likes and DMs.';
      if (/knowledge base|docs|q&a|chat/.test(t)) return 'Docs Q&A with source uploads and citation‑backed answers.';
      if (/booking|appointment|schedule|calendar/.test(t)) return 'Service booking with provider profiles and calendars.';
      const bodyTrim = (body || '').trim();
      const firstLines = bodyTrim.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 2);
      return firstLines.join(' ');
    };

    const pushCurrent = () => {
      if (!current) return;
      const body = current.buffer.join('\n').trim();
      if (!body) return;
      const cleanedTitle = cleanTitle(current.title);
      heads.push({
        title: cleanedTitle,
        description: friendlyDescription(cleanedTitle, body),
        text: body,
      });
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const h1Match = line.match(/^#\s+(.+?)\s*$/);
      if (h1Match) {
        const rawTitle = h1Match[1].trim();
        if (['Prompts template'].includes(rawTitle)) continue;
        const looksLikeTemplate = /:|\[.*\]|[A-Z]{3,}/.test(rawTitle) || /(Marketplace|Dashboard|Scheduling|Community|Knowledge Base|Downloads)/i.test(rawTitle);
        if (!looksLikeTemplate) continue;
        if (current) pushCurrent();
        current = { title: rawTitle.replace(/^\[|\]$/g, ''), buffer: [] };
      } else {
        if (current) current.buffer.push(line);
      }
    }
    pushCurrent();

    setItems(heads.slice(0, maxItems));
  }, [markdown, maxItems]);

  const handleViewAll = () => {
    navigate('/downloadable-prompts');
  };

  if (loading) {
    return null;
  }

  if (!items.length) {
    return null;
  }

  const isValidEmail = (email) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(String(email).toLowerCase());

  const startDownload = (section) => {
    const safe = section.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const blob = new Blob([`# ${section.title}\n\n${section.text}`], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe || 'prompt-template'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const requestEmailThenDownload = (section) => {
    setPendingDownload(section);
    setEmailErrorMessage('');
    const cached = localStorage.getItem('downloadable_prompts_email');
    if (cached && isValidEmail(cached)) {
      handleEmailSubmit(cached, section, false);
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (emailFromCacheOrInput, section = pendingDownload, closeModal = true) => {
    const email = emailFromCacheOrInput || emailInput;
    if (!isValidEmail(email)) {
      setEmailErrorMessage('Please enter a valid email address');
      return;
    }
    setIsSubmittingEmail(true);
    try {
      localStorage.setItem('downloadable_prompts_email', email);
      try { trackEvent('Homepage Preview Prompt Email Submitted', { template: section?.title }); } catch (e) {}
      const insertPayload = {
        email,
        template: section?.title || null,
        source: 'homepage_prompts_preview',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        created_at: new Date().toISOString()
      };
      await supabase.from('downloadable_prompt_downloads').insert(insertPayload);
    } catch (err) {
      try { trackEvent('Homepage Preview Prompt Email Submit Error', { message: err?.message || String(err) }); } catch (e) {}
      // Non-fatal
    } finally {
      setIsSubmittingEmail(false);
      if (closeModal) setIsEmailModalOpen(false);
      if (section) startDownload(section);
      setPendingDownload(null);
      setEmailInput('');
    }
  };

  return (
    <section className="py-6 md:py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Discover prompts from our users</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((s, idx) => (
            <motion.div
              key={s.title + idx}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.25) }}
              className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-vibe-cyan/20 flex items-center justify-center text-gray-700">✨</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 truncate">{s.title}</h3>
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 mb-2">
                    Optimized for Lovable
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{s.description}</p>
                </div>
              </div>
              <div className="pt-4 mt-auto">
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => requestEmailThenDownload(s)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-black/90"
                  >
                    <Download className="w-4 h-4" />
                    Copy prompt
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={handleViewAll}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50"
          >
            View All Community Prompts
            <span aria-hidden>↗</span>
          </button>
        </div>
      </div>

      {/* Minimal Email Capture Modal */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsEmailModalOpen(false)}></div>
          <div className="relative z-10 w-full max-w-md mx-auto rounded-2xl bg-white p-6 md:p-7 shadow-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold tracking-wide text-vibe-cyan bg-vibe-cyan/10 px-2.5 py-1 rounded-full">IT'S FREE</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Get this prompt</h3>
            <p className="text-gray-600 mb-5">Enter your email to download the template instantly.</p>
            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                  required
                />
                {emailErrorMessage ? (
                  <div className="text-sm text-red-600 mt-2">{emailErrorMessage}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={isSubmittingEmail}
                  onClick={() => handleEmailSubmit(undefined, pendingDownload)}
                  className="inline-flex items-center justify-center bg-black text-white px-5 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-all duration-200 disabled:opacity-70"
                >
                  {isSubmittingEmail ? 'Preparing…' : 'Download now'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEmailModalOpen(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Maybe later
                </button>
              </div>
              <p className="text-xs text-gray-500">No spam. We may send occasional product updates.</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CommunityPromptsPreview;


